import * as events from "utils/events";
import * as AgGrid from "ag-grid-community";
import * as columns from "../../grid/columns";
import * as datasource from "../../grid/datasource";
import * as gridUtils from "../../grid/utils";
import { DropdownFilter } from "grid/dropdown_filter";
import { DropdownEditor } from "grid/dropdown_editor";

import {
  AMOUNT_COL_DEF,
  AMOUNT_FILTER_COL_DEF,
  DATE_FILTER_COL_DEF,
  DATE_FILTER_UNTIL_NOW_COL_DEF,
  DEFAULT_COL_DEF,
  STATUS_COL_DEF,
  TEXT_FILTER_COL_DEF,
} from "../../grid/columns";
import { GridController } from "grid/grid_controller";

interface ReturnedPayment {
  comment: string;
  customer_name: string;
  due_amount: number;
  due_date: string;
  payment_amount: number;
  payment_date: string;
  payment_method: string;
  reference_number: string;
  return_user_id: string;
  status: string;
  status_orig: string;
  transmission_date: string;
}

export default class extends GridController<ReturnedPayment> {
  static targets = super.targets.concat(["searchInput"]);

  declare readonly hasSearchInputTarget: boolean;

  $searchInput: HTMLInputElement;

  override connect() {
    this.$searchInput = document.querySelector(".search-input");

    super.connect();

    const ctrl = this;
    events.subscribeOnEnter(this.$searchInput, () => ctrl.gridApi.onFilterChanged());
  }

  override buildGrid() {
    const ctrl = this;
    const {
      i18nJson,
      paginationPageSize,
      paymentMethodsJson,
      returnCodesJson,
      selectorPlaceholderLocales,
      statusesJson,
    } = this.gridTarget.dataset;
    const returnCodes = JSON.parse(returnCodesJson);

    const colDefs: AgGrid.ColDef[] = [
      {
        field: "status",
        ...STATUS_COL_DEF,
        sortable: true,
        ...columns.buildEnumFilterColDef(JSON.parse(statusesJson)),
      },
      { field: "due_date", ...DEFAULT_COL_DEF, ...DATE_FILTER_COL_DEF, sortable: true },
      { field: "account_number", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: true },
      { field: "due_amount", ...AMOUNT_COL_DEF, ...AMOUNT_FILTER_COL_DEF, sortable: true },
      {
        field: "payment_method",
        ...DEFAULT_COL_DEF,
        sortable: true,
        ...columns.buildEnumColDef(JSON.parse(paymentMethodsJson)),
        ...columns.buildEnumFilterColDef(JSON.parse(paymentMethodsJson)),
      },
      { field: "payment_date", ...DEFAULT_COL_DEF, ...DATE_FILTER_UNTIL_NOW_COL_DEF, sortable: true },
      { field: "payment_amount", ...AMOUNT_COL_DEF, ...AMOUNT_FILTER_COL_DEF, sortable: true },
      { field: "reference_number", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: true },
      {
        field: "transmission_date",
        ...DEFAULT_COL_DEF,
        ...DATE_FILTER_UNTIL_NOW_COL_DEF,
        sortable: true,
        sort: "desc",
      },
      { field: "customer_name", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: true },
      { field: "return_user_id", ...DEFAULT_COL_DEF, ...TEXT_FILTER_COL_DEF, sortable: false },
      { field: "comment", ...DEFAULT_COL_DEF, sortable: false },
      {
        field: "return_code",
        ...DEFAULT_COL_DEF,
        cellRenderer(params) {
          return returnCodes[params.value];
        },
        editable: true,
        tooltipValueGetter: (params) => returnCodes[params.value],
        filter: DropdownFilter,
        filterParams: {
          returnCodes,
          selectorPlaceholderLocales: JSON.parse(selectorPlaceholderLocales),
        },
        sortable: true,
      },
    ];

    const dataSource = new datasource.Datasource({
      limit: paginationPageSize,
      callbacks: {
        getSearchInput() {
          return ctrl.$searchInput.value;
        },
      },
    });

    const options: AgGrid.GridOptions<ReturnedPayment> = {
      ...gridUtils.DEFAULT_GRID_OPTIONS,
      // override defaults
      cacheBlockSize: parseInt(paginationPageSize),
      columnDefs: gridUtils.buildColumnDefs(colDefs, { i18n: JSON.parse(i18nJson) }),
      datasource: dataSource,
    };
    const gridApi = AgGrid.createGrid(this.gridTarget, options);
    dataSource.gridApi = gridApi;

    return gridApi;
  }
}
